import { GDPRContext } from "@/contexts/GDPRContext";
import { Contract } from "@/types/contract";
import { useParams, usePathname } from "next/navigation";
import { useContext } from "react";
import { getBackUrl, getContractUrl, getNextUrl } from "@/router";
import { EnumerationsContext } from "@/contexts/EnumerationsContext";
import { Product } from "@/types/enumerations";

type ProcessRouterConfig = {
  pathname: string;
  gdprAgreed: boolean;
  params: { id: string };
  products: Product[];
  isContractRequest: boolean;
};

type RouterResponse<T> = {
  getNextUrl: (item: T) => string | null;
  getBackUrl: (item: T) => string | null;
};

const createContractRouter = (config: ProcessRouterConfig): RouterResponse<Contract> => ({
  getNextUrl: (contract: Contract) => {
    const uuid = contract.uuid ?? String(config.params.id);
    const nextUrl = getNextUrl(contract, config.pathname, {
      gdprAgreed: config.gdprAgreed,
      products: config.products,
      isRequest: config.isContractRequest,
    });

    if (nextUrl) {
      return getContractUrl(uuid, nextUrl, config.isContractRequest);
    }

    return null;
  },
  getBackUrl: (contract: Contract) => {
    const uuid = contract.uuid ?? String(config.params.id);
    const backUrl = getBackUrl(contract, config.pathname, {
      products: config.products,
      isRequest: config.isContractRequest,
    });

    if (backUrl) {
      return getContractUrl(uuid, backUrl, config.isContractRequest);
    }

    return null;
  },
});

export const useProcessRouter = () => {
  const pathname = usePathname();
  const { gdprAgreed } = useContext(GDPRContext);
  const params = useParams();
  const { products } = useContext(EnumerationsContext);

  const config: ProcessRouterConfig = {
    pathname,
    gdprAgreed,
    products,
    params: { id: String(params.id) },
    isContractRequest: pathname.includes("/request") === true,
  };

  const contractRouter = createContractRouter(config);

  return {
    getNextUrl: contractRouter.getNextUrl,
    getBackUrl: contractRouter.getBackUrl,
  };
};
